import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import { GetCirclebyID } from "../../Apis/organizationApi";
import { useColonyStore } from "./store/useColonyStore";
import Select from "react-select";

bouncy.register();

const COLONY_FORBIDDEN_CHARS_PATTERN = /[@#$%^&*<>]/;
const COLONY_ONLY_NUMBERS_PATTERN = /^[0-9\s]+$/;
const COLONY_NAME_PATTERN = /^[a-zA-Z0-9\s\-]+$/;

const AddEditColony = ({
  showModal,
  setShowModal,
  editRow,
  isEditMode,
  zoneData,
}) => {
  const { colonies, isSaving, saveColony } = useColonyStore();
  const [wardData, setWardData] = useState([]);
  const [loadingWards, setLoadingWards] = useState(false);

  const fetchWardsByZone = async (zoneId) => {
    if (!zoneId) {
      setWardData([]);
      return;
    }
    setLoadingWards(true);
    try {
      const response = await GetCirclebyID(zoneId);
      setWardData(response.filter((circle) => circle.isActive));
    } catch (error) {
      console.error("Error fetching wards:", error);
      setWardData([]);
      toast.error("Failed to load wards for selected zone.");
    } finally {
      setLoadingWards(false);
    }
  };

  const getDefaultZoneId = () => {
    if (isEditMode) return editRow?.zoneId?.toString() || "";
    return zoneData?.[0]?.zoneId?.toString() || "";
  };

  useEffect(() => {
    if (!showModal) return;

    if (isEditMode && editRow?.zoneId) {
      fetchWardsByZone(editRow.zoneId);
      return;
    }

    if (!isEditMode && zoneData?.length > 0) {
      fetchWardsByZone(zoneData[0].zoneId);
    } else if (!isEditMode) {
      setWardData([]);
    }
  }, [showModal, isEditMode, editRow, zoneData]);

  const handleClose = () => {
    setShowModal(false);
    setWardData([]);
  };

  const initialValues = {
    zoneId: getDefaultZoneId(),
    circleId: isEditMode ? editRow?.circleId?.toString() || "" : "",
    colonyName: isEditMode ? editRow?.colonyName || "" : "",
    isActive: isEditMode ? editRow?.isActive?.toString() || "true" : "true",
  };

  const validationSchema = Yup.object().shape({
    zoneId: Yup.string().required("Zone is Required"),
    circleId: Yup.string().required("Ward is Required"),
    colonyName: Yup.string()
      .trim()
      .required("Colony Name is Required")
      .min(3, "Minimum 3 characters required.")
      .max(100, "Maximum 100 characters allowed.")
      .test(
        "not-only-numbers",
        "Colony Name cannot contain only numbers.",
        (value) => !value || !COLONY_ONLY_NUMBERS_PATTERN.test(value)
      )
      .test(
        "no-special-chars",
        "Special characters such as @, #, $, %, ^, &, *, <, > shall not be allowed.",
        (value) =>
          !value ||
          (!COLONY_FORBIDDEN_CHARS_PATTERN.test(value) &&
            COLONY_NAME_PATTERN.test(value))
      )
      .test(
        "unique-colony",
        "Colony name already exists for this ward",
        function (value) {
          const { circleId } = this.parent;
          if (!value || !circleId) return true;
          const trimmedName = value.trim();
          return !colonies.some(
            (item) =>
              item.colonyName?.toLowerCase() === trimmedName.toLowerCase() &&
              Number(item.circleId) === Number(circleId) &&
              item.colonyId !== editRow?.colonyId
          );
        }
      ),
    isActive: Yup.string().required("Status is Required"),
  });

  const handleSubmit = async (values) => {
    if (isEditMode && !editRow?.colonyCode) {
      toast.error("Colony code is missing. Please refresh and try again.");
      return;
    }

    let requestPayload;

    if (isEditMode) {
      requestPayload = {
        colonyId: editRow.colonyId,
        colonyName: values.colonyName.trim(),
        colonyCode: editRow.colonyCode,
        zoneId: Number(values.zoneId),
        circleId: Number(values.circleId),
        geoJsonPath: editRow.geoJsonPath || "string",
        isActive: values.isActive === "true",
      };
    } else {
      requestPayload = {
        colonyName: values.colonyName.trim(),
        zoneId: Number(values.zoneId),
        circleId: Number(values.circleId),
        geoJsonPath: "string",
        isActive: values.isActive === "true",
      };
    }

    const result = await saveColony(requestPayload, isEditMode);

    if (result?.success !== false) {
      handleClose();
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="lg"
      aria-labelledby="colony-modal-title"
      centered
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title id="colony-modal-title">
          {isEditMode ? "Edit Colony" : "Add Colony"}
        </Modal.Title>
        <i
          className="fa-solid fa-xmark close-container"
          onClick={handleClose}
        ></i>
      </Modal.Header>
      <hr />
      <Modal.Body>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >

          {({ setFieldValue, values }) => (
            <Form>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="zoneId" className="form-label">
                      Select Zone <span className="text-danger">*</span>
                    </label>

                    <Select
                      inputId="zoneId"
                      name="zoneId"
                      placeholder="Select Zone"
                      isSearchable
                      isClearable
                      options={
                        zoneData?.map((zone) => ({
                          value: String(zone.zoneId),
                          label: zone.zoneName,
                        })) || []
                      }
                      value={
                        zoneData
                          ?.map((zone) => ({
                            value: String(zone.zoneId),
                            label: zone.zoneName,
                          }))
                          .find(
                            (option) =>
                              String(option.value) === String(values.zoneId)
                          ) || null
                      }
                      onChange={(selectedOption) => {
                        const zoneId = selectedOption
                          ? selectedOption.value
                          : "";

                        setFieldValue("zoneId", zoneId);
                        setFieldValue("circleId", "");
                        fetchWardsByZone(zoneId);
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          backgroundColor: "#e9ecef",
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused
                            ? "#d6d8db"
                            : "#e9ecef",
                          color: "#212529",
                        }),
                      }}
                    />
                  </div>
                </div>

                {/* {({ setFieldValue, values }) => (
            <Form>
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="zoneId" className="form-label">
                      Select Zone <span className="text-danger">*</span>
                    </label>
                    <select
                    
                    inputId="zoneId"
                    isClearable
                    isSearchable
                    options={}
                    value={}
                    onChange={(e)=>{
                        const zoneId = e.target.value;
                        setFieldValue("zoneId", zoneId);
                        setFieldValue("circleId", "");
                        fetchWardsByZone(zoneId);
                    }}
                    styles={{
                        control: (base) => ({
                            ...base,
                            backgroundColor: "#e9ecef",
                            minHeight: "38px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            boxShadow: "none",
                        }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                        }),
                    }}
                    />
                    <Field
                      as="select"
                      name="zoneId"
                      id="zoneId"
                      className="form-select"
                      onChange={(e) => {
                        const zoneId = e.target.value;
                        setFieldValue("zoneId", zoneId);
                        setFieldValue("circleId", "");
                        fetchWardsByZone(zoneId);
                      }}
                    >
                      {zoneData?.map((zone) => (
                        <option key={zone.zoneId} value={zone.zoneId}>
                          {zone.zoneName}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      className="small required-message"
                      name="zoneId"
                      component="div"
                    />
                  </div>
                </div> */}

                {/* <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="circleId" className="form-label">
                      Select Ward <span className="text-danger">*</span>
                    </label>
                    <Field as="select" name="circleId" id="circleId" className="form-select">
                      <option value="">Select</option>
                      {loadingWards ? (
                        <option>Loading wards...</option>
                      ) : (
                        wardData.map((ward) => (
                          <option key={ward.circleId} value={ward.circleId}>
                            {ward.circleName}
                          </option>
                        ))
                      )}
                    </Field>
                    <ErrorMessage
                      className="small required-message"
                      name="circleId"
                      component="div"
                    />
                  </div>
                </div> */}
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="circleId" className="form-label">
                      Select Ward <span className="text-danger">*</span>
                    </label>

                    <Field name="circleId">
                      {({ field, form }) => (
                        <Select
                          inputId="circleId"
                          name="circleId"
                          placeholder="Select Ward"
                          isSearchable
                          isClearable
                          isLoading={loadingWards}
                          options={
                            wardData?.map((ward) => ({
                              value: String(ward.circleId),
                              label: ward.circleName,
                            })) || []
                          }
                          value={
                            wardData
                              ?.map((ward) => ({
                                value: String(ward.circleId),
                                label: ward.circleName,
                              }))
                              .find(
                                (option) =>
                                  String(option.value) === String(field.value)
                              ) || null
                          }
                          onChange={(selectedOption) => {
                            form.setFieldValue(
                              "circleId",
                              selectedOption ? selectedOption.value : ""
                            );
                          }}
                          onBlur={() => {
                            form.setFieldTouched("circleId", true);
                          }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              backgroundColor: "#e9ecef",
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isFocused
                                ? "#d6d8db"
                                : "#e9ecef",
                              color: "#212529",
                            }),
                          }}
                        />
                      )}
                    </Field>

                    <ErrorMessage
                      className="small required-message"
                      name="circleId"
                      component="div"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="colonyName" className="form-label">
                      Colony Name <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="colonyName"
                      id="colonyName"
                      placeholder="Enter Colony Name"
                      className="form-control"
                      maxLength={100}
                      readOnly={isEditMode}
                      onChange={(e) => {
                        const formattedValue = e.target.value
                          .slice(0, 100)
                          .replace(/^\s+/, "")
                          .replace(/\s{2,}/g, " ")
                          .replace(COLONY_FORBIDDEN_CHARS_PATTERN, "")
                          .replace(/[^a-zA-Z0-9\s\-]/g, "");
                        setFieldValue("colonyName", formattedValue);
                      }}
                    />
                    <ErrorMessage
                      className="small required-message"
                      name="colonyName"
                      component="div"
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <div className="form-group d-flex">
                    <div className="form-check d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="activeRadio"
                        value="true"
                      />
                      <label className="form-check-label" htmlFor="activeRadio">
                        Active
                      </label>
                    </div>
                    <div className="form-check ms-4 d-flex">
                      <Field
                        type="radio"
                        className="form-check-input"
                        name="isActive"
                        id="inactiveRadio"
                        value="false"
                      />
                      <label className="form-check-label" htmlFor="inactiveRadio">
                        InActive
                      </label>
                    </div>
                  </div>
                  <ErrorMessage
                    className="small required-message"
                    name="isActive"
                    component="div"
                  />
                </div>
              </div>

              <div className="text-end d-flex justify-content-center">
                <button type="submit" className="deployment-button">
                  {isEditMode ? (
                    isSaving ? (
                      <l-bouncy size="25" speed="1.75" color="white"></l-bouncy>
                    ) : (
                      <>
                        <i className="fa-solid fa-check done-container"></i> Update
                      </>
                    )
                  ) : isSaving ? (
                    <l-bouncy size="25" speed="1.75" color="white"></l-bouncy>
                  ) : (
                    <>
                      <i className="fa-solid fa-check done-container"></i> Save
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditColony;
